/**
 * ui.ts — Componentes reutilizables de UI para el panel admin/coach
 * Sprint 6: Hardening UX
 */
import { authService } from '../services/auth.service';
import { getTyrisIcon } from '../components/ui/tyris-icon';

// ─── EmptyState ───────────────────────────────────────────────────────────────

export interface EmptyStateOptions {
  icon?: string;
  title: string;
  description?: string;
  actionLabel?: string;
  actionHref?: string;
  actionId?: string;
}

export function renderEmptyState(opts: EmptyStateOptions): string {
  const icon = opts.icon ?? getTyrisIcon('404-not-found', { size: 48 });
  const action = opts.actionLabel
    ? opts.actionHref
      ? `<a href="${opts.actionHref}" class="btn btn-primary" style="margin-top:1rem;">${opts.actionLabel}</a>`
      : `<button id="${opts.actionId ?? 'empty-action'}" class="btn btn-primary" style="margin-top:1rem;">${opts.actionLabel}</button>`
    : '';

  return `
    <div class="empty-state" style="text-align:center;padding:3rem 1.5rem;color:var(--text-muted);">
      <div style="display:flex;justify-content:center;margin-bottom:1rem;color:var(--tyris-iris);">${icon}</div>
      <h3 style="font-size:1.1rem;font-weight:600;color:var(--text-main);margin-bottom:0.5rem;">${opts.title}</h3>
      ${opts.description ? `<p style="font-size:0.9rem;max-width:360px;margin:0 auto;">${opts.description}</p>` : ''}
      ${action}
    </div>
  `;
}

// ─── Loader ───────────────────────────────────────────────────────────────────

export function renderLoader(message = 'Cargando...'): string {
  return `
    <div class="ui-loader" style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding:3rem;gap:1rem;color:var(--text-muted);">
      <div style="width:2.5rem;height:2.5rem;border:3px solid var(--bg-surface-glow);border-top-color:var(--color-primary);border-radius:50%;animation:spin 0.8s linear infinite;"></div>
      <span style="font-size:0.9rem;">${message}</span>
    </div>
  `;
}

export function renderInlineLoader(): string {
  return `<span style="display:inline-block;width:1rem;height:1rem;border:2px solid rgba(255,255,255,0.2);border-top-color:currentColor;border-radius:50%;animation:spin 0.8s linear infinite;vertical-align:middle;margin-right:0.4rem;"></span>`;
}

// ─── Error State ──────────────────────────────────────────────────────────────

export function renderErrorState(message: string, retryId?: string): string {
  const retry = retryId
    ? `<button id="${retryId}" class="btn btn-outline" style="margin-top:1rem;display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('en-curso', { size: 16 })} Reintentar</button>`
    : '';
  return `
    <div class="ui-error-state" style="text-align:center;padding:3rem 1.5rem;color:var(--color-error);">
      <div style="display:flex;justify-content:center;margin-bottom:1rem;">${getTyrisIcon('alerta', { size: 48 })}</div>
      <p style="font-size:0.95rem;max-width:400px;margin:0 auto;">${message}</p>
      ${retry}
    </div>
  `;
}

// ─── Badge ────────────────────────────────────────────────────────────────────

export type BadgeVariant = 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'admin' | 'coach' | 'student';

const BADGE_COLORS: Record<BadgeVariant, string> = {
  success: 'background:hsla(142,70%,45%,0.15);color:hsl(142,70%,55%);border:1px solid hsla(142,70%,45%,0.3)',
  error:   'background:hsla(350,80%,55%,0.15);color:hsl(350,80%,65%);border:1px solid hsla(350,80%,55%,0.3)',
  warning: 'background:hsla(38,90%,50%,0.15);color:hsl(38,90%,60%);border:1px solid hsla(38,90%,50%,0.3)',
  info:    'background:hsla(200,80%,50%,0.15);color:hsl(200,80%,60%);border:1px solid hsla(200,80%,50%,0.3)',
  neutral: 'background:hsla(215,15%,70%,0.1);color:hsl(215,15%,70%);border:1px solid hsla(215,15%,70%,0.2)',
  admin:   'background:hsla(270,90%,60%,0.15);color:hsl(270,90%,70%);border:1px solid hsla(270,90%,60%,0.3)',
  coach:   'background:hsla(200,80%,50%,0.15);color:hsl(200,80%,60%);border:1px solid hsla(200,80%,50%,0.3)',
  student: 'background:hsla(142,70%,45%,0.15);color:hsl(142,70%,55%);border:1px solid hsla(142,70%,45%,0.3)',
};

export function renderBadge(text: string, variant: BadgeVariant = 'neutral'): string {
  const style = BADGE_COLORS[variant];
  return `<span style="${style};padding:0.2rem 0.6rem;border-radius:var(--radius-full);font-size:0.75rem;font-weight:600;white-space:nowrap;">${text}</span>`;
}

// ─── Role Badge ───────────────────────────────────────────────────────────────

export function renderRoleBadge(role: 'ADMIN' | 'COACH' | 'STUDENT'): string {
  const map: Record<string, BadgeVariant> = { ADMIN: 'admin', COACH: 'coach', STUDENT: 'student' };
  return renderBadge(role, map[role] ?? 'neutral');
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export interface ModalOptions {
  id: string;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmId?: string;
  cancelId?: string;
  danger?: boolean;
}

export function renderModal(opts: ModalOptions): string {
  const confirmStyle = opts.danger
    ? 'background:var(--color-error);color:#fff;border:none;'
    : 'background:var(--color-primary);color:#fff;border:none;';
  const confirmId = opts.confirmId ?? `${opts.id}-confirm`;
  const cancelId = opts.cancelId ?? `${opts.id}-cancel`;

  return `
    <div id="${opts.id}" class="modal-overlay" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;align-items:center;justify-content:center;">
      <div class="modal-box" style="background:var(--bg-surface);border:1px solid var(--border-metallic);border-radius:var(--radius-lg);padding:2rem;max-width:480px;width:90%;animation:slideUp 0.2s ease;">
        <h3 style="font-size:1.1rem;font-weight:700;margin-bottom:1rem;">${opts.title}</h3>
        <div style="color:var(--text-muted);font-size:0.9rem;margin-bottom:1.5rem;">${opts.body}</div>
        <div style="display:flex;gap:0.75rem;justify-content:flex-end;">
          <button id="${cancelId}" class="btn btn-outline">${opts.cancelLabel ?? 'Cancelar'}</button>
          <button id="${confirmId}" class="btn" style="${confirmStyle}padding:0.6rem 1.2rem;border-radius:var(--radius-sm);cursor:pointer;font-weight:600;">${opts.confirmLabel ?? 'Confirmar'}</button>
        </div>
      </div>
    </div>
  `;
}

export function showModal(id: string): void {
  const el = document.getElementById(id);
  if (el) { el.style.display = 'flex'; }
}

export function hideModal(id: string): void {
  const el = document.getElementById(id);
  if (el) { el.style.display = 'none'; }
}

export function bindModal(opts: {
  modalId: string;
  confirmId?: string;
  cancelId?: string;
  onConfirm: () => void | Promise<void>;
}): void {
  const confirmId = opts.confirmId ?? `${opts.modalId}-confirm`;
  const cancelId = opts.cancelId ?? `${opts.modalId}-cancel`;

  document.getElementById(cancelId)?.addEventListener('click', () => hideModal(opts.modalId));
  document.getElementById(opts.modalId)?.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).id === opts.modalId) hideModal(opts.modalId);
  });
  document.getElementById(confirmId)?.addEventListener('click', async () => {
    hideModal(opts.modalId);
    await opts.onConfirm();
  });
}

// ─── Page Header ──────────────────────────────────────────────────────────────

export interface PageHeaderOptions {
  title: string;
  subtitle?: string;
  actions?: string; // HTML de botones/acciones
  breadcrumb?: { label: string; href?: string }[];
}

export function renderPageHeader(opts: PageHeaderOptions): string {
  const breadcrumb = opts.breadcrumb?.length
    ? `<nav style="font-size:0.8rem;color:var(--text-muted);margin-bottom:0.5rem;">
        ${opts.breadcrumb.map((b, i) =>
          b.href
            ? `<a href="${b.href}" style="color:var(--text-muted);text-decoration:none;">${b.label}</a>${i < opts.breadcrumb!.length - 1 ? ' <span style="opacity:0.4;">›</span> ' : ''}`
            : `<span>${b.label}</span>`
        ).join('')}
       </nav>`
    : '';

  return `
    <div class="dashboard-header" style="margin-bottom:1.5rem;">
      <div class="dashboard-title">
        ${breadcrumb}
        <h2>${opts.title}</h2>
        ${opts.subtitle ? `<p>${opts.subtitle}</p>` : ''}
      </div>
      ${opts.actions ? `<div style="display:flex;gap:0.75rem;align-items:center;flex-wrap:wrap;">${opts.actions}</div>` : ''}
    </div>
  `;
}

// ─── Table helpers ────────────────────────────────────────────────────────────

export function renderTableWrapper(content: string): string {
  return `<div class="table-wrapper" style="overflow-x:auto;border-radius:var(--radius-md);border:1px solid var(--border-metallic);">${content}</div>`;
}

// ─── Role guard helper (UI) ───────────────────────────────────────────────────

export function renderAccessDenied(): string {
  return renderEmptyState({
    icon: getTyrisIcon('darse-de-baja', { size: 48 }),
    title: 'Acceso restringido',
    description: 'No tienes permisos para realizar esta acción.',
  });
}

// ─── Role / Permission Convenience Helpers ────────────────────────────────────

export function isAdmin(): boolean {
  return authService.hasRole(['ADMIN']);
}

export function isCoach(): boolean {
  return authService.hasRole(['COACH']);
}

// ─── Reusable Confirm Modal Helper ────────────────────────────────────────────

export function renderConfirmModal(opts: {
  id: string;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
}): string {
  return renderModal({
    id: opts.id,
    title: opts.title,
    body: opts.body,
    confirmLabel: opts.confirmLabel ?? 'Confirmar',
    cancelLabel: 'Cancelar',
    danger: opts.danger ?? false,
  });
}
